import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ClipboardCheck, ChevronRight, Calendar, MapPin, Play, Plus, X } from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'
import api from '../../services/api'

const TEAL   = '#00D4AA'
const ORANGE = '#FF9500'
const RED    = '#FF3B5C'
const BLUE   = '#0A84FF'
const GREEN  = '#00D4AA'
const DIM    = 'rgba(255,255,255,0.3)'

const STATUS_META: Record<string, { color: string; label: string }> = {
  scheduled:   { color: BLUE,   label: 'Scheduled' },
  in_progress: { color: ORANGE, label: 'In Progress' },
  completed:   { color: TEAL,   label: 'Completed' },
  cancelled:   { color: DIM,    label: 'Cancelled' },
}

const AUDIT_TYPES = [
  { value: 'whs',          label: 'WHS',          color: RED,    emoji: '🦺' },
  { value: 'environmental',label: 'Environmental', color: GREEN,  emoji: '🌿' },
  { value: 'quality',      label: 'Quality',       color: BLUE,   emoji: '✅' },
  { value: 'contractor',   label: 'Contractor',    color: ORANGE, emoji: '🏗️' },
  { value: 'fire_safety',  label: 'Fire Safety',   color: RED,    emoji: '🔥' },
  { value: 'emergency',    label: 'Emergency',     color: ORANGE, emoji: '🚨' },
]

const FILTERS = [
  ['scheduled',   'Scheduled'],
  ['in_progress', 'In Progress'],
  ['completed',   'Completed'],
  ['all',         'All'],
]

export default function MobileAudits() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [audits, setAudits] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('scheduled')

  // New audit sheet state
  const [showNew, setShowNew] = useState(false)
  const [auditType, setAuditType] = useState('whs')
  const [templateId, setTemplateId] = useState('')
  const [scheduledDate, setScheduledDate] = useState(new Date().toISOString().slice(0, 10))
  const [templates, setTemplates] = useState<any[]>([])
  const [sites, setSites] = useState<any[]>([])
  const [siteId, setSiteId] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')

  const load = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (filter !== 'all') params.set('status', filter)
      const { data } = await api.get(`/audits?${params}&limit=50`)
      setAudits(data.data || [])
      setTotal(data.total || 0)
    } catch {}
    setLoading(false)
  }

  useEffect(() => { load() }, [filter])

  const openNew = async () => {
    setAuditType('whs')
    setTemplateId('')
    setScheduledDate(new Date().toISOString().slice(0, 10))
    setSiteId('')
    setCreateError('')
    setShowNew(true)
    // Load templates and sites in parallel
    try {
      const [tmplRes, siteRes] = await Promise.all([
        api.get('/audits/templates').catch(() => ({ data: { data: [] } })),
        api.get('/organisation/sites').catch(() => ({ data: [] })),
      ])
      setTemplates(tmplRes.data.data || [])
      setSites(Array.isArray(siteRes.data) ? siteRes.data : [])
    } catch {}
  }

  const handleCreate = async () => {
    setCreating(true)
    setCreateError('')
    try {
      const payload: any = {
        audit_type: auditType,
        auditor_id: user!.id,
        scheduled_date: scheduledDate,
      }
      if (templateId) payload.template_id = templateId
      if (siteId) payload.site_id = siteId

      const { data } = await api.post('/audits', payload)
      setShowNew(false)
      load()
      // Navigate straight to conduct if starting today
      const today = new Date().toISOString().slice(0, 10)
      if (scheduledDate === today) {
        navigate(`/m/audits/${data.id}/conduct`)
      }
    } catch (err: any) {
      setCreateError(err.response?.data?.error || 'Failed to create audit')
    }
    setCreating(false)
  }

  const filteredTemplates = templates.filter(t => t.audit_type === auditType)

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100%', paddingBottom: 96 }}>
      {/* Sticky header */}
      <div
        className="sticky top-0 z-10"
        style={{ background: 'var(--bg)', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '16px 20px 12px' }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#FFFFFF', margin: 0 }}>Audits</h1>
          <span style={{ fontSize: 12, fontWeight: 500, color: DIM }}>{total} total</span>
        </div>
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', scrollbarWidth: 'none' }}>
          {FILTERS.map(([val, label]) => {
            const active = filter === val
            return (
              <button
                key={val}
                onClick={() => setFilter(val)}
                style={{
                  padding: '6px 14px',
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 600,
                  flexShrink: 0,
                  border: 'none',
                  cursor: 'pointer',
                  background: active ? TEAL : 'rgba(255,255,255,0.08)',
                  color: active ? '#FFFFFF' : 'rgba(255,255,255,0.45)',
                  transition: 'all 150ms',
                }}
              >
                {label}
              </button>
            )
          })}
        </div>
      </div>

      {/* List */}
      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '64px 0', color: DIM }}>Loading…</div>
        ) : audits.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '80px 0', gap: 12 }}>
            <div style={{ width: 60, height: 60, borderRadius: '50%', background: `${TEAL}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ClipboardCheck size={26} color={TEAL} />
            </div>
            <p style={{ fontWeight: 600, fontSize: 15, color: '#FFFFFF', margin: 0 }}>No audits</p>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', margin: 0 }}>
              {filter === 'all' ? 'No audits recorded' : `No ${filter.replace('_', ' ')} audits`}
            </p>
            <button
              onClick={openNew}
              style={{ marginTop: 4, padding: '10px 20px', borderRadius: 999, fontSize: 13, fontWeight: 600, color: '#FFFFFF', background: TEAL, border: 'none', cursor: 'pointer' }}
            >
              Schedule Audit
            </button>
          </div>
        ) : (
          audits.map(audit => {
            const canConduct = audit.status === 'scheduled' || audit.status === 'in_progress'
            const meta = STATUS_META[audit.status] || STATUS_META.scheduled
            const scoreColor = audit.score == null ? null
              : audit.score >= 80 ? TEAL : audit.score >= 60 ? ORANGE : RED

            return (
              <div
                key={audit.id}
                style={{ background: 'var(--white)', borderRadius: 18, borderLeft: `3px solid ${meta.color}`, overflow: 'hidden' }}
              >
                <div style={{ padding: '14px 14px 14px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 10, fontFamily: 'monospace', fontWeight: 600, color: 'rgba(255,255,255,0.4)' }}>
                        {audit.audit_number}
                      </span>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, fontWeight: 600, background: `${meta.color}20`, color: meta.color }}>
                        {meta.label}
                      </span>
                    </div>
                    {audit.score != null && (
                      <span style={{ fontWeight: 900, fontSize: 18, color: scoreColor!, flexShrink: 0 }}>
                        {audit.score}%
                      </span>
                    )}
                  </div>

                  <p style={{ fontWeight: 600, fontSize: 13, color: '#FFFFFF', margin: '0 0 8px', lineHeight: 1.4 }}>
                    {audit.template_name || (audit.audit_type?.replace(/_/g, ' ') ?? 'Audit')}
                  </p>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>
                    {audit.site_name && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <MapPin size={10} />{audit.site_name}
                      </span>
                    )}
                    {audit.scheduled_date && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Calendar size={10} />
                        {new Date(audit.scheduled_date).toLocaleDateString('en-AU')}
                      </span>
                    )}
                  </div>
                </div>

                {canConduct ? (
                  <button
                    onClick={() => navigate(`/m/audits/${audit.id}/conduct`)}
                    style={{ width: '100%', padding: '11px 14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, fontSize: 13, fontWeight: 700, border: 'none', borderTop: '1px solid rgba(255,255,255,0.06)', background: 'none', color: TEAL, cursor: 'pointer' }}
                    className="active:opacity-75 transition-opacity"
                  >
                    <Play size={13} fill={TEAL} color={TEAL} />
                    {audit.status === 'in_progress' ? 'Continue Audit' : 'Start Audit'}
                  </button>
                ) : (
                  <button
                    onClick={() => navigate(`/m/audits/${audit.id}`)}
                    style={{ width: '100%', padding: '11px 14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, fontSize: 13, fontWeight: 500, border: 'none', borderTop: '1px solid rgba(255,255,255,0.06)', background: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer' }}
                    className="active:opacity-75 transition-opacity"
                  >
                    View Report <ChevronRight size={13} />
                  </button>
                )}
              </div>
            )
          })
        )}
      </div>

      {/* FAB */}
      <button
        onClick={openNew}
        style={{
          position: 'fixed',
          bottom: 'calc(64px + env(safe-area-inset-bottom) + 16px)',
          right: 20,
          width: 52,
          height: 52,
          borderRadius: '50%',
          background: TEAL,
          boxShadow: `0 0 24px ${TEAL}55`,
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          zIndex: 50,
        }}
        className="active:scale-95 transition-transform"
      >
        <Plus size={24} color="white" />
      </button>

      {/* New Audit bottom sheet */}
      {showNew && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.6)' }}
            onClick={() => setShowNew(false)}
          />
          <div
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 101,
              background: '#0F1823',
              borderRadius: '24px 24px 0 0',
              border: '1px solid rgba(255,255,255,0.08)',
              borderBottom: 'none',
              maxHeight: '88vh',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Fixed handle + header */}
            <div style={{ flexShrink: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 12 }}>
                <div style={{ width: 36, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.15)' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 20px 14px' }}>
                <span style={{ fontSize: 20, fontWeight: 800, color: '#FFFFFF' }}>New Audit</span>
                <button
                  onClick={() => setShowNew(false)}
                  style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '50%', width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                >
                  <X size={16} color="rgba(255,255,255,0.5)" />
                </button>
              </div>
            </div>

            {/* Scrollable form body */}
            <div style={{ flex: 1, overflowY: 'scroll', WebkitOverflowScrolling: 'touch' as any, overscrollBehavior: 'contain', padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Audit type grid */}
              <div>
                <p style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 10px' }}>Audit Type</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                  {AUDIT_TYPES.map(t => {
                    const active = auditType === t.value
                    return (
                      <button
                        key={t.value}
                        onClick={() => { setAuditType(t.value); setTemplateId('') }}
                        style={{
                          padding: '10px 6px',
                          borderRadius: 14,
                          border: `1.5px solid ${active ? t.color : 'rgba(255,255,255,0.08)'}`,
                          background: active ? `${t.color}20` : 'var(--white)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 4,
                          cursor: 'pointer',
                        }}
                      >
                        <span style={{ fontSize: 20 }}>{t.emoji}</span>
                        <span style={{ fontSize: 11, fontWeight: 600, color: active ? t.color : 'rgba(255,255,255,0.5)', textAlign: 'center', lineHeight: 1.2 }}>{t.label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Template selector — only if templates exist for this type */}
              {filteredTemplates.length > 0 && (
                <div>
                  <p style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>Template (optional)</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <button
                      onClick={() => setTemplateId('')}
                      style={{ padding: '10px 12px', borderRadius: 12, border: `1.5px solid ${!templateId ? TEAL : 'rgba(255,255,255,0.08)'}`, background: !templateId ? `${TEAL}15` : 'var(--white)', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 500, color: !templateId ? TEAL : 'rgba(255,255,255,0.45)' }}>No template (ad-hoc)</span>
                    </button>
                    {filteredTemplates.map((t: any) => (
                      <button
                        key={t.id}
                        onClick={() => setTemplateId(t.id)}
                        style={{ padding: '10px 12px', borderRadius: 12, border: `1.5px solid ${templateId === t.id ? TEAL : 'rgba(255,255,255,0.08)'}`, background: templateId === t.id ? `${TEAL}15` : 'var(--white)', textAlign: 'left', cursor: 'pointer' }}
                      >
                        <span style={{ fontSize: 13, fontWeight: 500, color: templateId === t.id ? TEAL : '#FFFFFF' }}>{t.name}</span>
                        {t.question_count > 0 && (
                          <span style={{ display: 'block', fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>{t.question_count} questions</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Date */}
              <div>
                <p style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>Date</p>
                <input
                  type="date"
                  value={scheduledDate}
                  onChange={e => setScheduledDate(e.target.value)}
                  style={{ fontSize: 16, width: '100%' }}
                />
              </div>

              {/* Site selector */}
              {sites.length > 0 && (
                <div>
                  <p style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>Site (optional)</p>
                  <select value={siteId} onChange={e => setSiteId(e.target.value)} style={{ fontSize: 16 }}>
                    <option value="">— Any site —</option>
                    {sites.map((s: any) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Spacer so last field isn't flush against footer */}
              <div style={{ height: 8 }} />
            </div>

            {/* Pinned footer — always visible */}
            <div style={{ flexShrink: 0, padding: '12px 16px', paddingBottom: 'calc(64px + max(20px, env(safe-area-inset-bottom)))', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
              {createError && (
                <div style={{ padding: '10px 12px', borderRadius: 12, background: `${RED}18`, color: RED, fontSize: 13, marginBottom: 10 }}>
                  {createError}
                </div>
              )}
              <button
                onClick={handleCreate}
                disabled={creating}
                style={{
                  width: '100%',
                  padding: '14px',
                  borderRadius: 16,
                  fontSize: 15,
                  fontWeight: 700,
                  color: '#FFFFFF',
                  background: creating ? 'rgba(255,255,255,0.15)' : TEAL,
                  border: 'none',
                  cursor: creating ? 'default' : 'pointer',
                  boxShadow: creating ? 'none' : `0 0 20px ${TEAL}40`,
                }}
                className="active:scale-[0.98] transition-transform"
              >
                {creating ? 'Creating…' : scheduledDate === new Date().toISOString().slice(0, 10) ? 'Start Audit Now' : 'Schedule Audit'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
